import { parseISO } from 'date-fns/parseISO';
import memoize from 'lodash/memoize';
import Localisation, {
  currency as currencyFeatures,
  date as dateFeatures,
  number as numberFeatures,
} from 'saddlebag-localisation';

import currencySymbols from './currency-symbols';
import { DATE_FORMAT } from './date-formats';

import type { Culture } from '@web-platform/shared-travel-tools-types/src/culture';

type DateTime = Date | string;
type Logger = { error: (msg: string) => void };

const NON_LOCALISED_FORMATS = [
  DATE_FORMAT.NON_LOCALISED_SHORT,
  DATE_FORMAT.NON_LOCALISED_YEAR_MONTH,
  DATE_FORMAT.NON_LOCALISED_FULL,
];

// By default, memoize function only uses the first argument provided to the memoized function as the cache key.
// But for localisation formatting functionality, the second argument will also change the result, so we need to take it as part of the cache keys.
const cacheKeyResolver = (...args: unknown[]) => JSON.stringify(args);

const parseDate = (date: DateTime): Date => (typeof date === 'string' ? parseISO(date) : date);

export const LocalisationService = (cldrInfo: unknown, culture: Culture, logger: Logger) => {
  try {
    Localisation.enhance(currencyFeatures, numberFeatures, dateFeatures);
    const saddlebag = Localisation(cldrInfo);
    return {
      formatCurrency: memoize(
        (value: number, format = 'c0') => saddlebag.formatCurrency(value, format),
        cacheKeyResolver,
      ),
      formatCurrencyFull: memoize(
        (value: number, format = 'c2') => saddlebag.formatCurrencyFull(value, format),
        cacheKeyResolver,
      ),
      formatNumber: memoize(
        (value: number | string, decimals = 1) => saddlebag.formatNumber(value, decimals),
        cacheKeyResolver,
      ),
      formatDate: memoize((date: DateTime, format = DATE_FORMAT.SHORT) => {
        const definitelyDate = parseDate(date);
        if (NON_LOCALISED_FORMATS.includes(format)) {
          return saddlebag.formatDateNonlocalised(definitelyDate, format);
        }
        return saddlebag.formatDate(definitelyDate, format);
      }, cacheKeyResolver),
      getFirstDay: () => saddlebag.getFirstDay(),
      getDaysOfWeek: () => saddlebag.getDaysOfWeek(),
      getMonthName: (date: DateTime, format = 'wide') =>
        saddlebag.getMonthName(parseDate(date), format),
      monthNames: (format = 'wide') => saddlebag.monthNames(format),
      getDatePlaceholder: () => saddlebag.getDatePlaceholder(),
      culture,
    };
  } catch (err) {
    const stack = err instanceof Error ? err.stack : undefined;
    const msg = `Caught an exception when saddlebag localisation initialization: ${err}\n${stack}`;
    logger.error(msg);

    const { currency } = culture;
    const symbolMap: Record<string, string> = currencySymbols;
    const symbol = symbolMap[currency] ?? currency;
    if (!symbol) {
      throw new Error(`Currency ${currency} cannot be fallback`);
    }

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sept',
      'Oct',
      'Nov',
      'Dec',
    ];
    const daysOfWeek = [
      {
        name: 'Monday',
        nameAbbr: 'Mon',
        nameShort: 'Mo',
        index: 1,
        cldrKey: 'mon',
        isWeekend: false,
      },
      {
        name: 'Tuesday',
        nameAbbr: 'Tue',
        nameShort: 'Tu',
        index: 2,
        cldrKey: 'tue',
        isWeekend: false,
      },
      {
        name: 'Wednesday',
        nameAbbr: 'Wed',
        nameShort: 'We',
        index: 3,
        cldrKey: 'wed',
        isWeekend: false,
      },
      {
        name: 'Thursday',
        nameAbbr: 'Thu',
        nameShort: 'Th',
        index: 4,
        cldrKey: 'thu',
        isWeekend: false,
      },
      {
        name: 'Friday',
        nameAbbr: 'Fri',
        nameShort: 'Fr',
        index: 5,
        cldrKey: 'fri',
        isWeekend: false,
      },
      {
        name: 'Saturday',
        nameAbbr: 'Sat',
        nameShort: 'Sa',
        index: 6,
        cldrKey: 'sat',
        isWeekend: true,
      },
      {
        name: 'Sunday',
        nameAbbr: 'Sun',
        nameShort: 'Su',
        index: 0,
        cldrKey: 'sun',
        isWeekend: true,
      },
    ];

    const formatNumber = (value: number | string, decimals = 1) => {
      const digits = Number(value).toFixed(decimals).split('.');
      const decimal = digits[1];
      const integerChar = digits[0].split('').reverse();
      const integer = integerChar.reduce((result, char, index) => {
        if (index % 3 === 0 && index !== 0) return `${char},${result}`;
        return `${char}${result}`;
      }, '');
      return decimal ? `${integer}.${decimal}` : String(integer);
    };

    return {
      formatCurrency: (value: number, format = 'c0') => {
        const digits = Number(format.substring(1));
        return `${symbol}${formatNumber(Math.ceil(value), digits)}`;
      },
      formatCurrencyFull: (value: number, format = 'c2') => {
        const digits = Number(format.substring(1));
        return `${symbol}${formatNumber(value, digits)}`;
      },
      formatNumber,
      // Without CLDR data the dates read in English: the formats that name a month draw the
      // abbreviated name `monthNames` holds, and the rest fall back to ISO. `parseISO` also
      // accepts a `yyyy-MM` string.
      formatDate: (date: DateTime, format: string = DATE_FORMAT.SHORT) => {
        const dateObj = parseDate(date);
        const year = dateObj.getFullYear();
        const monthName = monthNames[dateObj.getMonth()];
        const day = dateObj.getDate();
        switch (format) {
          case DATE_FORMAT.LONG:
          case DATE_FORMAT.MEDIUM:
            return `${day} ${monthName} ${year}`;
          case DATE_FORMAT.YEAR_MONTH:
          case DATE_FORMAT.YEAR_MONTH_ABBR:
            return `${monthName} ${year}`;
          case DATE_FORMAT.MONTH_DAY:
          case DATE_FORMAT.MONTH_DAY_NO_WEEK_ABBR:
            return `${day} ${monthName}`;
          default: {
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            return `${year}-${month}-${String(day).padStart(2, '0')}`;
          }
        }
      },
      getFirstDay: () => 0,
      getDaysOfWeek: () => daysOfWeek,
      getMonthName: (date: DateTime) => monthNames[parseDate(date).getMonth()],
      monthNames: () => monthNames,
      getDatePlaceholder: () => 'dd/mm/yyyy',
      culture,
    };
  }
};

export type LocalisationServiceShape = ReturnType<typeof LocalisationService>;
